import { z } from "zod";

// ─── ParsedMbankTransactionSchema ─────────────────────────────────────────────
//
// In-memory parser output returned from parseMbankPdf().
// Amounts are numbers (signed float) at this stage.
// booking_date and operation_date are ISO 8601 strings "YYYY-MM-DD".

export const ParsedMbankTransactionSchema = z.object({
  booking_date: z.string().date(),
  operation_date: z.string().date(),
  description: z.string().min(1),
  amount: z.number(),
  balance: z.number(),
  type: z.enum(["incoming", "outgoing"]),
  invoice_number: z.string().nullable(),
  nip: z.string().nullable(),
  counterparty: z.string().nullable(),
});

export type ParsedMbankTransaction = z.infer<typeof ParsedMbankTransactionSchema>;

// ─── ParsedMbankTransactionDtoSchema ──────────────────────────────────────────
//
// API response DTO — amounts serialized as decimal strings (NUMERIC convention).

export const ParsedMbankTransactionDtoSchema = z.object({
  booking_date: z.string().date(),
  operation_date: z.string().date(),
  description: z.string(),
  amount: z.string().regex(/^-?\d+\.\d{2}$/),
  balance: z.string().regex(/^-?\d+\.\d{2}$/),
  type: z.enum(["incoming", "outgoing"]),
  invoice_number: z.string().nullable(),
  nip: z.string().nullable(),
  counterparty: z.string().nullable(),
});

export type ParsedMbankTransactionDto = z.infer<typeof ParsedMbankTransactionDtoSchema>;

// ─── PdfUploadResponseSchema ───────────────────────────────────────────────────
//
// Full response shape for POST /api/statements/upload with a PDF file.

export const PdfUploadResponseSchema = z.object({
  statement: z.object({
    id: z.string().uuid(),
    account_type: z.enum(["client", "operational"]),
    statement_date: z.string().date(),
    file_name: z.string(),
    status: z.enum(["processed", "error"]),
    uploaded_at: z.string().datetime({ offset: true }),
    processed_at: z.string().datetime({ offset: true }).nullable(),
  }),
  transactions: z.array(ParsedMbankTransactionDtoSchema),
  warnings: z.array(z.string()),
});

export type PdfUploadResponse = z.infer<typeof PdfUploadResponseSchema>;
